// src/pages/crypto-outflows.ts —— USDT 收付「支出事件 / Outflows」页签（批 7a，审计 O-11 / P-12）。
// 由 crypto.ts 以一行挂接（文案并入 I18N_CRYPTO、视图追加到 PAGES_CRYPTO.cryptoPayments.views）；本文件不 import crypto.ts（避免加载环），
// table 域函数只在调用期使用（页文件与 table 域互相加载时不在求值期触碰其绑定）。
import type { RouteKey } from '../../../shared/src/api/routes';
import { ApiError } from '../../../shared/src/api/client';
import { fmtDateTime } from '../../../shared/src/time';
import type { AdminCtx } from '../domains/types';
import { str } from '../domains/types';
import { openDrawer, openForm, closeDrawer, reloadCurrent, sessionGuard } from '../domains/table';
import { defineView, type DrawerAction, type ErasedView, type FormDef, type Need } from './types';
import { cell, chip, localErr, type CellVals, type ChipKind } from './ui';

const APPROVE: Need = { module: 'cryptoPayments', bit: 'approve' };

/** GET /api/admin/crypto-outflows 一行（02 §8.22.4）。金额为原子整数字符串 + 精度，不经浮点。 */
export type OutflowStatus = 'detected' | 'matched' | 'unknown' | 'mismatch' | 'reorg';
export type OutflowRow = {
  id: number;
  network: string;
  chain: string;
  tx_hash: string;
  log_index: number;
  from_registry_id: number;
  from_role: 'deposit' | 'payout';
  from_label?: string | null;
  from_address: string;
  to_address: string;
  raw_amount: string;
  token_decimals: number;
  token_contract?: string;
  block_number: number;
  block_hash?: string;
  block_time?: string | null;
  source: 'webhook' | 'scan';
  status: OutflowStatus;
  match_reason?: string | null;
  matched_ref?: string | null;
  first_seen_at?: string | null;
  verified_at?: string | null;
  classified_at?: string | null;
  alert_raised_at?: string | null;
  pause_applied_at?: string | null;
  reviewed_by?: number | null;
  reviewed_account?: string | null;
  reviewed_at?: string | null;
  review_note?: string;
  version: number;
  payout_paused_at?: string | null;
};
/** POST /api/admin/crypto-outflows/{id}/review 成功响应。 */
export type OutflowReviewResult = { id: number; status: string; version: number; pause_cleared: boolean };

export const I18N_OUTFLOW = {
  zh: {
    cpOutflows: '支出事件', ofFromAddress: '转出地址', ofToAddress: '收款方', ofRole: '地址用途', ofRole_deposit: '收款池', ofRole_payout: '付款池',
    ofSt_detected: '待判定', ofSt_matched: '已匹配授权', ofSt_unknown: '未授权转出', ofSt_mismatch: '授权不符', ofSt_reorg: '已重组',
    ofReason: '判定原因', ofRef: '授权记录', ofRefNone: '无对应授权记录', ofRef_treasury_transfer: '财务转账', ofRef_withdrawal: '用户提币单', ofRef_agent_withdrawal: '代理提佣单',
    ofSource: '发现途径', ofSource_webhook: '回调', ofSource_scan: '兜底补扫', ofBlock: '区块', ofBlockTime: '区块时间', ofFirstSeen: '首次发现', ofVerifiedAt: '最近核验',
    ofAlertAt: '告警时间', ofPausedAt: '地址暂停时间', ofPayoutPaused: '付款池暂停信号', ofPayoutPausedYes: '暂停中（财务付款池拒绝签名）', ofPayoutPausedNo: '未暂停',
    ofReviewed: '人工复核', ofReviewedNo: '未复核', ofReview: '复核异常转出', ofReviewNote: '复核结论', ofReviewNoteBad: '请填写复核结论（不超过 1000 字）',
    ofReviewTip: '复核前请先核对链上交易与授权材料。复核只记录结论：付款池地址在全部异常转出复核后解除暂停信号；收款池地址不会自动恢复分配，须在地址抽屉另行开启。复核不撤销链上交易，也不补登授权记录。',
    ofReviewed_ok: '已记录复核结论', ofReviewed_cleared: '已记录复核结论，付款池暂停信号已解除', ofErrVersion: '该事件已被他人更新，请刷新后重试', ofErrNotReviewable: '该事件不需要复核（已复核，或为已匹配 / 待判定事件）',
    ofHash: '交易哈希', ofDetail: '支出事件详情', ofRules: '平台登记的收款池 / 付款池地址每笔 USDT 转出都独立核链并与授权付款记录匹配；未授权或不符即 Critical 告警并暂停该地址（收款池关闭分配、付款池拒绝签名），须人工复核。金额以链上回执原子值为准，回调自报金额不采信。注意：收款地址转给已批准且生效的付款地址视为内部调拨，不停分配；其他任何转出（包括业主用 TP 钱包转到其他地址）都会被判为未授权转出，立即停止该地址分配；复核后须回到地址抽屉重新开启分配。',
    cpErrUnreviewedOutflow: '该地址有未复核的异常转出，复核后才能重新开启分配（见「支出事件」页签）',
  },
  en: {
    cpOutflows: 'Outflows', ofFromAddress: 'From address', ofToAddress: 'Recipient', ofRole: 'Address purpose', ofRole_deposit: 'Deposit pool', ofRole_payout: 'Payout pool',
    ofSt_detected: 'Pending check', ofSt_matched: 'Matched authorization', ofSt_unknown: 'Unauthorized outflow', ofSt_mismatch: 'Authorization mismatch', ofSt_reorg: 'Reorged',
    ofReason: 'Reason', ofRef: 'Authorization record', ofRefNone: 'No matching authorization', ofRef_treasury_transfer: 'Treasury transfer', ofRef_withdrawal: 'User withdrawal', ofRef_agent_withdrawal: 'Agent withdrawal',
    ofSource: 'Detected via', ofSource_webhook: 'Webhook', ofSource_scan: 'Backstop scan', ofBlock: 'Block', ofBlockTime: 'Block time', ofFirstSeen: 'First seen', ofVerifiedAt: 'Last verified',
    ofAlertAt: 'Alerted at', ofPausedAt: 'Address paused at', ofPayoutPaused: 'Payout pause signal', ofPayoutPausedYes: 'Paused (treasury refuses to sign)', ofPayoutPausedNo: 'Not paused',
    ofReviewed: 'Manual review', ofReviewedNo: 'Not reviewed', ofReview: 'Review outflow', ofReviewNote: 'Review conclusion', ofReviewNoteBad: 'Enter a review conclusion (up to 1000 characters)',
    ofReviewTip: 'Check the on-chain transaction and authorization evidence first. A review only records the conclusion: a payout address is unpaused once all of its abnormal outflows are reviewed; a deposit address is not re-enabled automatically and must be re-enabled in the address drawer. Reviewing neither reverts the transaction nor registers an authorization.',
    ofReviewed_ok: 'Review recorded', ofReviewed_cleared: 'Review recorded; payout pause signal cleared', ofErrVersion: 'This event was changed by someone else. Refresh and retry', ofErrNotReviewable: 'This event does not need review (already reviewed, matched or still pending)',
    ofHash: 'Tx hash', ofDetail: 'Outflow detail', ofRules: 'Every USDT outflow from a registered deposit or payout address is verified on chain and matched against authorized payout records. Unauthorized or mismatched outflows raise a Critical alert and pause the address (deposit: allocation off; payout: treasury refuses to sign) until reviewed. Amounts come from the on-chain receipt; webhook amounts are never trusted. Note: transfers from a deposit address to an approved, effective payout address are internal transfers and keep allocation on; ANY other outflow (including the owner moving funds elsewhere in TP Wallet) is flagged as unauthorized and stops allocation; after review, re-enable allocation in the address drawer.',
    cpErrUnreviewedOutflow: 'This address has unreviewed abnormal outflows. Review them (Outflows tab) before re-enabling allocation',
  },
} as const;

const ofText = (c: AdminCtx, k: string): string => str(c.t, k) || String(Reflect.get(I18N_OUTFLOW[c.lang], k) ?? k);
const when = (c: AdminCtx, iso: string | null | undefined): string => fmtDateTime(iso, { tz: c.s.sh.timezone || undefined });

const STATUS_CHIP: Readonly<Record<OutflowStatus, ChipKind>> = { detected: 'blue', matched: 'green', unknown: 'red', mismatch: 'red', reorg: 'gray' };
export const outflowStatusLabel = (c: AdminCtx, s: string): string => (s in STATUS_CHIP ? ofText(c, `ofSt_${s}`) : s);
const statusChip = (c: AdminCtx, s: OutflowStatus): CellVals => (s in STATUS_CHIP ? chip(STATUS_CHIP[s], outflowStatusLabel(c, s)) : cell(s));

/** 原子整数 → 十进制全精度字符串（不经浮点、不舍入）；非法输入返回 —。 */
export function outflowAmount(raw: string | undefined, decimals: number | undefined): string {
  if (!raw || !/^(0|[1-9][0-9]*)$/.test(raw) || decimals === undefined || !Number.isInteger(decimals) || decimals < 0 || decimals > 36) return '—';
  if (decimals === 0) return raw;
  const s = raw.padStart(decimals + 1, '0');
  const f = s.slice(-decimals).replace(/0+$/, '');
  return s.slice(0, -decimals) + (f ? '.' + f : '');
}

/** matched_ref（`<kind>:<id>`）→「财务转账 #12」等；未知种类原样展示；空 → 无对应授权记录。 */
export function outflowRefLabel(c: AdminCtx, ref: string | null | undefined): string {
  if (!ref) return ofText(c, 'ofRefNone');
  const m = /^([a-z_]+):([1-9][0-9]*)$/.exec(ref);
  if (!m) return ref;
  const label = str(c.t, `ofRef_${m[1]}`) || (Reflect.get(I18N_OUTFLOW[c.lang], `ofRef_${m[1]}`) as string | undefined);
  return label ? `${label} #${m[2]}` : ref;
}

/** 待复核：未复核且为 unknown / mismatch，或曾触发暂停（后被更正 / 重组）。与服务端 ReviewOutflow 前置条件一致。 */
export const outflowReviewable = (r: OutflowRow): boolean => !r.reviewed_at && (r.status === 'unknown' || r.status === 'mismatch' || !!r.pause_applied_at);

/** 复核接口错误 → 本页文案；其余原样抛出交给外壳统一提示。 */
export function outflowErr(c: AdminCtx, route: RouteKey, e: unknown): unknown {
  if (!(e instanceof ApiError) || e.code !== 1012) return e;
  if (e.data.reason === 'outflow_version_changed') return localErr(route, ofText(c, 'ofErrVersion'));
  if (e.data.reason === 'outflow_not_reviewable') return localErr(route, ofText(c, 'ofErrNotReviewable'));
  return e;
}

/** 复核表单（cryptoPayments.approve）：只读事件要点，必填结论；提交 `{version, note}`。 */
export function outflowReviewForm(c: AdminCtx, r: OutflowRow): FormDef {
  return {
    title: ofText(c, 'ofReview'),
    tip: ofText(c, 'ofReviewTip'),
    fields: [
      { k: 'status', label: ofText(c, 'cpStatus'), type: 'readonly' },
      { k: 'from', label: ofText(c, 'ofFromAddress'), type: 'readonly', wide: true },
      { k: 'to', label: ofText(c, 'ofToAddress'), type: 'readonly', wide: true },
      { k: 'amount', label: ofText(c, 'cpAmount'), type: 'readonly' },
      { k: 'hash', label: ofText(c, 'ofHash'), type: 'readonly', wide: true },
      { k: 'note', label: ofText(c, 'ofReviewNote'), type: 'text', wide: true },
    ],
    init: { status: outflowStatusLabel(c, r.status), from: r.from_address, to: r.to_address, amount: `${outflowAmount(r.raw_amount, r.token_decimals)} USDT`, hash: r.tx_hash, note: '' },
    validate: (v) => {
      const note = String(v['note'] ?? '').trim();
      return note && note.length <= 1000 ? '' : ofText(c, 'ofReviewNoteBad');
    },
    submit: async (ctx, v) => {
      const alive = sessionGuard(ctx);
      const route: RouteKey = 'POST /api/admin/crypto-outflows/{id}/review';
      let out: OutflowReviewResult;
      try {
        out = await ctx.gp.api.call<OutflowReviewResult>(route, { path: { id: r.id }, body: { version: r.version, note: String(v['note']).trim() } });
      } catch (e) {
        throw outflowErr(ctx, route, e);
      }
      if (!alive()) return;
      ctx.gp.ui.toast(ofText(ctx, out.pause_cleared ? 'ofReviewed_cleared' : 'ofReviewed_ok'));
      closeDrawer(ctx);
      await reloadCurrent(ctx);
    },
  };
}

/** 支出事件抽屉：链上证据、判定结论与授权记录、告警 / 暂停 / 复核时间线；待复核时提供「复核异常转出」。 */
export function outflowDrawer(c: AdminCtx, r: OutflowRow): void {
  const actions: DrawerAction[] = [];
  if (outflowReviewable(r)) actions.push({ label: ofText(c, 'ofReview'), kind: 'primary', need: APPROVE, run: (ctx) => openForm(ctx, outflowReviewForm(ctx, r)) });
  const reviewed = r.reviewed_at ? `${when(c, r.reviewed_at)} · ${r.reviewed_account || (r.reviewed_by ? `#${r.reviewed_by}` : '—')} · ${r.review_note ?? ''}` : ofText(c, 'ofReviewedNo');
  const fields = [
    { k: ofText(c, 'cpStatus'), v: outflowStatusLabel(c, r.status) },
    { k: ofText(c, 'ofReason'), v: r.match_reason || '—' },
    { k: ofText(c, 'ofRef'), v: outflowRefLabel(c, r.matched_ref) },
    { k: ofText(c, 'cpNetwork'), v: r.network },
    { k: ofText(c, 'ofRole'), v: ofText(c, `ofRole_${r.from_role}`) },
    { k: ofText(c, 'cpAmount'), v: `${outflowAmount(r.raw_amount, r.token_decimals)} USDT` },
    { k: ofText(c, 'cpRaw'), v: r.raw_amount },
    { k: ofText(c, 'ofFromAddress'), v: `${r.from_label ? r.from_label + ' · ' : ''}${r.from_address}`, wide: true },
    { k: ofText(c, 'ofToAddress'), v: r.to_address, wide: true },
    { k: ofText(c, 'ofHash'), v: `${r.tx_hash} #${r.log_index}`, wide: true },
    { k: ofText(c, 'ofBlock'), v: `${r.block_number}${r.block_hash ? ' · ' + r.block_hash : ''}`, wide: true },
    { k: ofText(c, 'ofBlockTime'), v: when(c, r.block_time) },
    { k: ofText(c, 'ofSource'), v: ofText(c, `ofSource_${r.source}`) },
    { k: ofText(c, 'ofFirstSeen'), v: when(c, r.first_seen_at) },
    { k: ofText(c, 'ofVerifiedAt'), v: when(c, r.verified_at) },
    { k: ofText(c, 'ofAlertAt'), v: when(c, r.alert_raised_at) },
    { k: ofText(c, 'ofPausedAt'), v: when(c, r.pause_applied_at) },
    ...(r.from_role === 'payout' ? [{ k: ofText(c, 'ofPayoutPaused'), v: r.payout_paused_at ? `${ofText(c, 'ofPayoutPausedYes')} · ${when(c, r.payout_paused_at)}` : ofText(c, 'ofPayoutPausedNo') }] : []),
    { k: ofText(c, 'ofReviewed'), v: reviewed, wide: true },
  ];
  openDrawer(c, { kind: 'crypto-outflow', title: `${ofText(c, 'ofDetail')} #${r.id}`, subtitle: `${r.network} · ${outflowStatusLabel(c, r.status)}`, fields, actions });
}

/** 「支出事件」页签视图：facet 网络 / 状态；列表列 网络、金额、转出地址、收款方、状态、授权记录、区块时间。 */
export const OUTFLOW_VIEW: ErasedView = defineView<OutflowRow>({
  key: 'outflows',
  source: { route: 'GET /api/admin/crypto-outflows', tabParam: 'none' },
  echo: '/admin/crypto-outflows',
  cols: [{ label: 'cpNetwork', facet: 'network' }, { label: 'cpAmount' }, { label: 'ofFromAddress' }, { label: 'ofToAddress' }, { label: 'cpStatus', facet: 'status', facetLabel: (t, v) => str(t, `ofSt_${v}`) || v }, { label: 'ofRef' }, { label: 'ofBlockTime' }],
  cells: (c, r) => [
    cell(r.network),
    cell(outflowAmount(r.raw_amount, r.token_decimals)),
    cell(r.from_address, r.from_label ?? ofText(c, `ofRole_${r.from_role}`), true),
    cell(r.to_address, undefined, true),
    statusChip(c, r.status),
    cell(outflowRefLabel(c, r.matched_ref), r.match_reason ?? undefined),
    cell(when(c, r.block_time)),
  ],
  open: outflowDrawer,
  rowActions: () => [
    { label: 'cpDetail', run: outflowDrawer },
    { label: 'ofReview', need: APPROVE, show: outflowReviewable, run: (ctx, r) => openForm(ctx, outflowReviewForm(ctx, r)) },
  ],
  noDate: true,
  noKeyword: true,
  noExport: true,
  csvName: 'crypto-outflows',
  note: (t) => str(t, 'ofRules'),
});
